import type { MouseEvent as ReactMouseEvent } from 'react';
import type {
  ClientIntelligence,
  FlagTone,
  NextActionKind,
} from '@/lib/clients/roster-intelligence';
import { cn } from '@/lib/utils';

interface RosterFlagBadgesProps {
  intelligence?: ClientIntelligence;
  /** Hide the next-best-action chip (e.g. in dense table cells). */
  hideAction?: boolean;
  /** Render only the next-best-action chip (row-level compact placement). */
  chipOnly?: boolean;
  /**
   * Makes the next-best-action chip clickable (Phase 3.6 routing). Receives the
   * action kind; the click never bubbles to the row's own click handler.
   */
  onAction?: (kind: NextActionKind) => void;
}

// Semantic status tokens (theme-aware across light/dark/glass/high-contrast) —
// same pattern the Dashboard status pills use. Avoids hardcoded palette scales.
const TONE_CLASSES: Record<FlagTone, string> = {
  info: 'border-status-info bg-status-info-surface text-status-info',
  warn: 'border-status-warn bg-status-warn-surface text-status-warn',
  danger: 'border-status-deviation bg-status-deviation-surface text-status-deviation',
};

const CHIP_CLASSES =
  'inline-flex items-center gap-1 rounded-full border border-dashed border-muted-foreground/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground';

/**
 * Phase 3 roster status flags + next-best-action chip. Renders nothing when the
 * client is on track and no action is recommended (keeps the roster calm).
 * With `onAction` the chip routes to the recommended workflow (Phase 3.6).
 */
export function RosterFlagBadges({
  intelligence,
  hideAction = false,
  chipOnly = false,
  onAction,
}: RosterFlagBadgesProps) {
  if (!intelligence) return null;

  const { flags, nextBestAction } = intelligence;
  const showAction = !hideAction && nextBestAction.kind !== 'review';
  const showFlags = !chipOnly;

  if ((!showFlags || flags.length === 0) && !showAction) return null;

  const handleChipClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    // Never toggle the row's own selection when acting on the chip.
    event.stopPropagation();
    onAction?.(nextBestAction.kind);
  };

  const chip = onAction ? (
    <button
      type="button"
      className={cn(
        CHIP_CLASSES,
        'cursor-pointer transition-colors hover:border-primary/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
      onClick={handleChipClick}
      data-testid="roster-next-action"
      aria-label={`Next step: ${nextBestAction.label}`}
    >
      <span aria-hidden>→</span>
      {nextBestAction.label}
    </button>
  ) : (
    <span className={CHIP_CLASSES} data-testid="roster-next-action">
      <span aria-hidden>→</span>
      {nextBestAction.label}
    </span>
  );

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="roster-flags">
      {showFlags &&
        flags.map((flag) => (
          <span
            key={flag.id}
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${TONE_CLASSES[flag.tone]}`}
          >
            {flag.label}
          </span>
        ))}
      {showAction && chip}
    </div>
  );
}
